import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

function PantrySection({ title, icon, sectionKey, rows, onChangeRow, onBlurName,
  onFocusStock, onBlurStock, stockEditing,
  onRemoveRow, onAddRow, fresh, t }) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="pantry-card">
      <div
        className={`pantry-header pantry-header--clickable${fresh ? ' pantry-header--fresh' : ''}`}
        onClick={() => setCollapsed((c) => !c)}
      >
        <h2>{icon} {title}</h2>
        <span className={`pantry-collapse-arrow${collapsed ? ' pantry-collapse-arrow--collapsed' : ''}`}>▾</span>
      </div>
      {!collapsed && <div className="pantry-body">
        {rows.length > 0 && (
          <div className="pantry-table-header">
            <span className="pantry-col-name">{t('pantry.ingredient')}</span>
            <span className="pantry-col-num">
              <span className="header-full">{t('pantry.stock')}</span>
              <span className="header-abbr">{t('pantry.stockAbbr')}</span>
            </span>
            <span className="pantry-col-num">
              <span className="header-full">{t('pantry.need')}</span>
              <span className="header-abbr">{t('pantry.need')}</span>
            </span>
            <span className="pantry-col-num">
              <span className="header-full">{t('pantry.left')}</span>
              <span className="header-abbr">{t('pantry.left')}</span>
            </span>
            <span className="pantry-col-action"></span>
          </div>
        )}
        {rows.map(({ _origIndex, ...row }) => {
          const stockEntry = stockEditing[_origIndex];
          const stockDisplay = stockEntry !== undefined ? stockEntry.current : row.qty;
          const effectiveNeed = row.need || 0;
          const left = (parseFloat(row.qty) || 0) - effectiveNeed;
          return (
            <div key={_origIndex} className={`pantry-row${row.qty === 0 ? ' pantry-row--empty' : ''}`}>
              <input
                className={`pantry-input pantry-input-name${fresh ? ' pantry-input-name--fresh' : ''}`}
                type="text"
                placeholder={t('pantry.ingredient')}
                value={row.name}
                onChange={(e) => onChangeRow(_origIndex, 'name', e.target.value)}
                onBlur={() => onBlurName(_origIndex)}
              />
              <input
                className={`pantry-input pantry-input-num${effectiveNeed > 0 && (parseFloat(row.qty) || 0) < effectiveNeed ? ' pantry-input-shortage' : ''}`}
                type="text"
                inputMode="decimal"
                placeholder="0"
                value={stockDisplay}
                onFocus={() => onFocusStock(_origIndex)}
                onBlur={() => onBlurStock(_origIndex)}
                onChange={(e) => onChangeRow(_origIndex, 'qty', e.target.value)}
              />
              <input
                className="pantry-input pantry-input-num pantry-input-readonly"
                type="text"
                value={effectiveNeed}
                readOnly
                tabIndex={-1}
              />
              <input
                className={`pantry-input pantry-input-num pantry-input-readonly${left < 0 ? ' pantry-input-shortage' : ''}`}
                type="text"
                value={Math.round(left * 100) / 100}
                readOnly
                tabIndex={-1}
              />
              <button
                type="button"
                className="inline-remove-btn"
                onClick={() => onRemoveRow(_origIndex)}
                title={t('pantry.removeTitle')}
              >
                ✕
              </button>
            </div>
          );
        })}
        <button type="button" className="inline-add-btn pantry-add-btn" onClick={() => onAddRow(sectionKey)}>
          {t('pantry.addIngredient')}
        </button>
      </div>}
    </div>
  );
}

function Pantry({ items, confirmed, saveError, onSave, onConfirm, onShowShopping }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState(items.length > 0 ? items.map((it) => ({ ...it })) : []);
  const saveTimeout = useRef(null);
  const editCountRef = useRef(0);
  const savedCountRef = useRef(0);
  const [toast, setToast] = useState(null);
  const toastTimeout = useRef(null);
  const [reviewDismissed, setReviewDismissed] = useState(false);

  const showToast = (msg) => {
    clearTimeout(toastTimeout.current);
    setToast(msg);
    toastTimeout.current = setTimeout(() => setToast(null), 2500);
  };

  useEffect(() => {
    if (editCountRef.current !== savedCountRef.current) return;
    setRows(items.length > 0 ? items.map((it) => ({ ...it })) : []);
  }, [items]);

  useEffect(() => {
    setReviewDismissed(false);
  }, [confirmed]);

  const toSaveFormat = (row) => ({
    ...row,
    qty: parseFloat(row.qty) || 0,
    need: parseFloat(row.need) || 0,
    is_dismissed_missing: row.is_dismissed_missing === true,

  });

  const scheduleSave = useCallback(
    (newRows) => {
      editCountRef.current += 1;
      clearTimeout(saveTimeout.current);
      const capturedCount = editCountRef.current;
      saveTimeout.current = setTimeout(() => {
        savedCountRef.current = capturedCount;
        onSave(newRows);
      }, 600);
    },
    [onSave]
  );

  const handleChange = (index, field, value) => {
    const updated = rows.map((row, i) =>
      i === index
        ? {
            ...row,
            [field]: value,
            ...(field === 'qty' && row.is_from_stock === false ? { is_from_stock: true } : {}),
          }
        : row
    );
    setRows(updated);
    if (field === 'qty') {
      setStockEditing((prev) => {
        if (!prev[index]) return prev;
        return { ...prev, [index]: { ...prev[index], current: value } };
      });
    }
    scheduleSave(updated.map(toSaveFormat));
  };

  const handleBlurName = (index) => {
    setAddingIndex(null);
    const name = rows[index]?.name?.trim().toLowerCase();
    if (!name) return;
    const isDuplicate = rows.some((r, i) => i !== index && r.name.trim().toLowerCase() === name);
    if (isDuplicate) {
      showToast(t('pantry.alreadyInPantry', { name: rows[index].name.trim() }));
      const updated = rows.filter((_, i) => i !== index);
      setRows(updated);
      scheduleSave(updated.map(toSaveFormat));
    }
  };

  const [stockEditing, setStockEditing] = useState({});

  const handleFocusStock = (index) => {
    setStockEditing((prev) => ({ ...prev, [index]: { original: rows[index].qty, current: '' } }));
  };

  const handleBlurStock = (index) => {
    setStockEditing((prev) => {
      const next = { ...prev };
      const entry = next[index];
      if (entry && entry.current === '') {
        const updated = rows.map((row, i) => i === index ? { ...row, qty: entry.original } : row);
        setRows(updated);
      }
      delete next[index];
      return next;
    });
  };

  const [addingIndex, setAddingIndex] = useState(null);

  const addRow = (section) => {
    setAddingIndex(rows.length);
    setRows((prev) => [...prev, { name: '', qty: 0, need: 0, section, is_staple: false, is_from_stock: true }]);
  };

  const removeRow = (index) => {
    const row = rows[index];
    if (row && row.is_from_stock === false) {
      const updated = rows.map((r, i) => (i === index ? { ...r, is_dismissed_missing: true } : r));
      setRows(updated);
      scheduleSave(updated.map(toSaveFormat));
      return;
    }
    const updated = rows.filter((_, i) => i !== index);
    setRows(updated);
    scheduleSave(updated.map(toSaveFormat));
  };

  const sorted = (sectionKey) =>
    rows
      .map((row, i) => ({ ...row, _origIndex: i }))
      .filter((row) => row.is_dismissed_missing !== true)
      .filter((row) => (row.section || 'cupboard') === sectionKey)
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

  const [hideUnavailable, setHideUnavailable] = useState(false);

  const filtered = (sectionKey) => {
    let list = sorted(sectionKey);
    if (hideUnavailable) {
      list = list.filter((row) => (parseFloat(row.qty) || 0) > 0 || (!row.name && row.qty === 0) || row._origIndex === addingIndex);
    }
    return list;
  };

  const showReview = !confirmed && !reviewDismissed && items.length > 0;

  return (
    <div className="pantry-sections">
      {toast && <div className="pantry-toast">{toast}</div>}
      {saveError && <div className="pantry-save-error" role="alert">{saveError}</div>}
      {showReview && (
        <div className="pantry-review-banner">
          <div className="pantry-review-text">
            <strong>{t('pantry.reviewTitle')}</strong>
            <span>{t('pantry.reviewSubtitle')}</span>
          </div>
          <div className="pantry-review-actions">
            <button type="button" className="pantry-review-confirm" onClick={onConfirm}>
              {t('pantry.looksGood')}
            </button>
            <button type="button" className="pantry-review-shopping" onClick={onShowShopping}>
              {t('pantry.wentShopping')}
            </button>
            <button type="button" className="pantry-review-dismiss" onClick={() => setReviewDismissed(true)}>
              {t('pantry.skip')}
            </button>
          </div>
        </div>
      )}
      <label className="pantry-toggle">
        <input
          type="checkbox"
          checked={hideUnavailable}
          onChange={(e) => setHideUnavailable(e.target.checked)}
        />
        {t('pantry.hideUnavailable')}
      </label>
      <PantrySection
        title={t('pantry.fresh')}
        icon="🥬"
        sectionKey="fresh"
        rows={filtered('fresh')}
        onChangeRow={handleChange}
        onBlurName={handleBlurName}
        onFocusStock={handleFocusStock}
        onBlurStock={handleBlurStock}
        stockEditing={stockEditing}
        onRemoveRow={removeRow}
        onAddRow={addRow}
        fresh
        t={t}
      />
      <PantrySection
        title={t('pantry.cupboard')}
        icon="🍯"
        sectionKey="cupboard"
        rows={filtered('cupboard')}
        onChangeRow={handleChange}
        onBlurName={handleBlurName}
        onFocusStock={handleFocusStock}
        onBlurStock={handleBlurStock}
        stockEditing={stockEditing}
        onRemoveRow={removeRow}
        onAddRow={addRow}
        t={t}
      />
    </div>
  );
}

export default Pantry;
